import {_, React, classNames, StyledProps, observer, Highlighter, classObserver, logJS, utils, Popover2, pluralize} from '@api'
import {Icon} from '@blueprintjs/core'
import {Tooltip2} from '@api'
import {ToggleInfoPanelButton} from '@modules/common/components/InfoCards/ToggleInfoPanelButton'
import {site, LibraryItemConfig, ICatalogFieldConfig, ICardAreaUIConfigImage, ICardUIConfig, ICardAreaUIConfig, WorkspaceConfig, IDataMapConfig, LibraryItemConfigWrapper, useCatalog, Stores, ImageRecord} from '@stores'
import {action, computed, observable, toJS} from 'mobx'
import {computedFn} from 'mobx-utils'
import {LazyAvatar, Placeholder, PrimaryIconButton, Badge, NumberBadge} from './'
import {DataMapper, DragEventPlaceholders, IDataMapped, ViewConfigMapping} from 'centric-ui-components'
import {ListItem, Typography, Card, CardContent, Tooltip, ListItemSecondaryAction, IconButton, CardActions} from '@material-ui/core'
import {Link, Check, Close, Add} from '@material-ui/icons'
import * as css from './ListPreviewCard.css'
import {BuilderCardLoader, ErrorBoundary, useCatalogItemsListStore} from '@modules'

interface MyProps extends StyledProps {
  stores: Partial<Stores>
  isSelected?: boolean
  data?: any
  viewStyle?: 'card' | 'flat' | 'pivot'
  showHex?: boolean
  /**
   * Active/Inactive text, usually we use a red diagonal, other times we use text
   */
  showActive?: boolean
  mapping?: Array<ViewConfigMapping>
  onClick?: (e, id) => void
  showImage?: boolean
  loading?: boolean
  size?: string
  level?: number // Where in the hierarchy the card is
  field?: ICatalogFieldConfig
  elevation?: number,
  badge1Text?: string,
  badge2Text?: string,
  draggable?: boolean,
  disabled?: boolean,
  avatar?: string,
  emptyPlaceHolder?: string
  titleCount?: number | React.ReactNode
  hideTooltips?: boolean,
  showClose?: boolean
  onClickClose?: (e) => void
  popperProps?: any
  toggleInfoPanel?: () => void
  infoPanelActive?: boolean
  library?: LibraryItemConfig
  hasLabel?: boolean
  titleActions?: React.ReactNode
  titleActionsLeft?: React.ReactNode
  showTooltip?: boolean
  offlineItemsCount?: React.ReactNode
  extra?: React.ReactNode
  more?: React.ReactNode
  cardDetails?: ICardUIConfig
  dataMap?: any
  searchText?: string
  canShowAssignedToBadge?: boolean
  workspace?: Partial<WorkspaceConfig>
  onContextMenu?: React.MouseEventHandler<any>
  hideEmptyImage?: boolean
}

const emptyPlaceHolder = '--'

@classObserver
export class ListPreviewCard extends React.Component<MyProps> {
  static whyDidYouRender = true

  constructor(props, state) {
    super(props, state)

    this.loadAssignedToValues()
  }

  @action loadAssignedToValues = async () => {
    const {props: {canShowAssignedToBadge, data, library}} = this
    if (canShowAssignedToBadge && library) {
      if (library.unassignedField) { // The library data itself has a single _assignedTo style field we can look at
        this.assignedValues = _.get(data, library.unassignedField)
      } else if (library.assignTo) {
        // Todo - this needs to be replaced with a query

        const {id, stores: {es, flexLayout: {activeCatalogRoot}}} = this
        if (activeCatalogRoot) {
          const {catalog: {index}} = activeCatalogRoot
          const {hits: {hits, total: {value: count}}} = await es.search({
            index: index,
            size: 100,
            meta: `Find places where ${library.index} '${id}' is assigned to at least one ${activeCatalogRoot.catalog.catalogItemName}`,
            body: {
              query: {term: {[`${library.assignTo}.id`]: id}},
              _source: ['']
            }
          }, {log: {query: false}})

          this.assignedValues = hits.map(({_id}) => ({index, id: _id}))
          // console.log(hits)
          //this.assignedValues = this.libraryWrapper.catalogItemByAssignTo[this.id]
        }
      }
    }
  }

  @observable.ref assignedValues: Array<{ id, index, prefixField? }> = []

  static defaultProps = {
    elevation: 1,
    draggable: false,
    showImage: true,
    showHex: true,
    size: 'medium',
    onClick: () => {
    },
    emptyPlaceHolder: emptyPlaceHolder,
    loading: false,
    showTooltip: true
  }

  @observable mouseOverAssignedTo: { id, index } = null

  onMouseEnterAssignedTo = ({id, index}) => e => {
    this.mouseOverAssignedTo = {id, index}
  }

  // onMouseLeaveAssignedTo = e => {
  //   this.mouseOverAssignedTo = null
  // }

  @computed get hideTooltips() {
    const {props: {stores, showTooltip, hideTooltips, viewStyle}} = this
    return showTooltip !== false && (hideTooltips || viewStyle == 'flat' || stores.dragDrop.isDragging)
  }

  @computed get cardDetails() {
    const {props: {cardDetails, library}} = this
    return cardDetails ?? library?.cardUI
  }

  @computed get catalog() {
    const {props: {workspace, stores, stores: {workspaces}}} = this
    if (workspace) {
      const ws = workspaces.wrapperById[workspace._id]
      return ws?.catalog ?? stores.catalog
    }

    return stores.catalog
  }

  @computed get primaryImageSrc() { return this.cardDetails?.main?.image?.field || 'images' }

  @computed get primaryImageDetails() { return _.get(this.props.data, this.primaryImageSrc) }

  @computed get secondary() { return this.cardDetails?.secondary }

  @computed get primaryAvatar() { return this.cardDetails?.main?.image?.avatar || this.props.avatar }

  @computed get dataMap(): IDataMapConfig {
    return this.props.dataMap ?? this.props.library?.dataMap ?? this.catalog.dataMapForLibrary(this.props.library)
  }

  @computed get workspace() {
    const {catalog, catalog: {workspaces}, props: {library, workspace}} = this
    return workspaces.wrapperById[(workspace ?? catalog.workspaceByIndex[library?.index] ?? catalog.workspaceConfig)?._id] ?? workspaces.workspace
  }

  @computed get activeFieldDef() {
    const {props: {library, showActive}, dataMap, workspace} = this

    //const activeFieldDef = library?.showStatusLabel && getLibraryFieldDef(library?.dataMap?.active, library?.index)
    return !dataMap?.active || showActive === false ? null :
           workspace ? workspace.catalog.fieldForWorkspace(dataMap.active, workspace.config) : this.catalog.getLibraryFieldDef(dataMap.active, library?.index)
  }

  fieldValueFor = computedFn((dataMapField: keyof IDataMapConfig) => {
    const {props: {data}, workspace, dataMap} = this

    let v = data[dataMapField] ?? data[dataMap[dataMapField] as string]

    if (_.isUndefined(v)) { v = _.get(data, dataMap[dataMapField] as string)}

    let r = v || v === '' ? v : null

    r = _.isString(r) ? _.trim(r) : r
    if (_.isNumber(r)) { r = r.toString() }

    const f = workspace.catalog._fieldByName[dataMap[dataMapField] as string]

    if (f?.def?.type === 'Ref' && f?.esIndex && r) {
      return <ListPreviewCardRefCatalogItem field={f} value={r}/>
    } else if (f?.def?.type === 'RefList' && f?.esIndex && !_.isEmpty(r)) {
      return <ListPreviewCardRefListCatalogItems field={f} value={r}/>
    }
    //logJS({r, f, dataMap, data})

    return r
  })

  get strings() { return this.catalog.strings}

  @computed get fieldValues() {
    const {strings, catalog, props: {data}, fieldValueFor, activeFieldDef} = this

    let fieldValues = {
      title: data && data.blank ? strings.unassigned : data.title || data.title === '' ? _.isString(data.title) ? _.trim(data.title) : data.title : fieldValueFor('title'),
      subtitle1: fieldValueFor('subtitle1'),
      subtitle2: fieldValueFor('subtitle2'),
      subtitle3: fieldValueFor('subtitle3'),
      active: activeFieldDef ? _.get(data, catalog.esFieldFor_byField(activeFieldDef.field)) : null
    }

    let {title} = fieldValues

    _.isNumber(title) && (fieldValues.title = title.toString())

    return fieldValues
  }

  get stores() { return this.catalog.stores}

  @computed get libraryWrapper(): LibraryItemConfigWrapper {
    const {stores, catalog, props: {library}} = this

    return stores.extra?.library ?? (library ? catalog.libraryById[library.id] : null)
  }

  @computed get assignedToCards() {
    const {props: {canShowAssignedToBadge, data, library}, catalog, stores: {flexLayout: {activeCatalogRoot}}} = this

    if (canShowAssignedToBadge && library) {
      const {assignedValues} = this

      if (!_.isEmpty(assignedValues)) {
        const first = _.first(assignedValues)

        return (
          <div
            className={css.assignedToCards}
            onClick={e => {
              // Removed functionality due to bug YFTS-7396
              // sidebar.right.updateState({ open: true, path: [{where: [{value: id}]}]})
              e.preventDefault()
              e.stopPropagation()

              //this.mouseOverAssignedTo = this.mouseOverAssignedTo === first ? null : first
            }}>
            <Popover2
              interactionKind='click'
              minimal={false}
              position='right'
              modifiers={{
                flip: {enabled: false}
              }}
              content={<ErrorBoundary>
                <BuilderCardLoader esIndex={first.index}
                                   id={first.id}
                                   levelOfDetail={1}
                                   disableSelection hideButtons isPopover/>
              </ErrorBoundary>}
            >
              <Tooltip title={`Assigned to ${assignedValues.length} ${pluralize(activeCatalogRoot?.catalog?.catalogItemName ?? 'Item', assignedValues.length)}`}>
                <Icon className={css.assignedToButton} icon='tick-circle' iconSize={12}
                  //onMouseEnter={this.onMouseEnterAssignedTo(first)}

                />
              </Tooltip>
            </Popover2>

            {false && <NumberBadge value={assignedValues.length} className={css.assignedToButton}
              //onMouseEnter={this.onMouseEnterAssignedTo(first)}
			                       onClick={e => {
                                     // Removed functionality due to bug YFTS-7396
                                     // sidebar.right.updateState({ open: true, path: [{where: [{value: id}]}]})
                                     e.preventDefault()
                                     e.stopPropagation()

                                     this.mouseOverAssignedTo = this.mouseOverAssignedTo === first ? null : first
                                   }}/>}

            {false && assignedValues.map(({id, index}, i) => id && (
              <Icon className={css.assignedToButton} icon='tick-circle' iconSize={12}
                    key={i}
                    onMouseEnter={this.onMouseEnterAssignedTo({id, index})}
                    onClick={e => {
                      // Removed functionality due to bug YFTS-7396
                      // sidebar.right.updateState({ open: true, path: [{where: [{value: id}]}]})
                      // e.preventDefault()
                      // e.stopPropagation()
                    }}/>))}
          </div>)
      }
    }

    return null
  }

  @computed get id() { return this.catalog.idFor(this.props.data)}

  @computed get primaryImageStyle() {
    const {workspace, cardDetails} = this
    return {
      '--swatch-size': cardDetails?.main?.image?.swatchSize ? `${cardDetails?.main?.image?.swatchSize}px` : undefined,
      '--card-image-padding': workspace?.catalog?.features?.card?.imagePadding,
      '--card-image-align-self': workspace?.catalog?.features?.card?.alignSelf
    } as any
  }

  @computed get primaryImage() : ImageRecord {
    const {primaryImageDetails} = this
    return _.isArray(primaryImageDetails) ? _.first(primaryImageDetails) : primaryImageDetails
  }

  render() {
    const {catalog, primaryImageStyle, activeFieldDef, assignedToCards, primaryImageDetails, secondary, primaryAvatar, fieldValues, fieldValues: {title, active, subtitle1, subtitle2, subtitle3}} = this
    let {
        props: {
          isSelected, level, extra, dataMap,
          more,
          className,
          data,
          showHex,
          onClick,
          showImage,
          elevation,
          badge1Text,
          badge2Text,
          draggable,
          disabled,
          avatar,
          emptyPlaceHolder, hideEmptyImage,
          titleCount,
          loading,
          viewStyle,
          hideTooltips,
          showClose,
          onClickClose,
          size,
          popperProps,
          library,
          showTooltip,
          field, classes, canShowAssignedToBadge,
          hasLabel, titleActions, titleActionsLeft,
          toggleInfoPanel, infoPanelActive, cardDetails,
          offlineItemsCount, searchText, workspace, stores, mapping, showActive, ...props
        }, maybeRenderPopover, primaryImage
      } = this,
      {strings} = catalog

    // console.log(`ListPreviewCard`, toJS({library, activeFieldDef, dataMap, title, subtitle1, subtitle2, subtitle3, active, data}))
    return (
      <ListItem
        {...props}
        {...{component: 'div'} as any}
        className={classNames(css.root, className, loading && css.loading)}
        data-view={viewStyle}
        data-style={viewStyle}
        data-level={level}
        data-object-fit={catalog.objectFitFor(this.workspace.config)}
        data-size={size}
        data-has-label={hasLabel}
        data-has-count={titleCount != null}
        data-type='ListPreviewCard'
        data-has-external-link={!_.isEmpty(data.exId)}
        data-active={_.isBoolean(active) ? active : undefined}
        // data-loading={loading}
        draggable={draggable}
        onClick={(e) => {
          onClick ? onClick(e, data._id) : null
        }}
        //onMouseLeave={this.onMouseLeaveAssignedTo}
        disabled={disabled}>
        {maybeRenderPopover(
          <Card
            draggable={draggable}
            data-type='Card'
            className={classNames(css.card, {
              [css.hasBackground]: cardDetails?.secondary?.image,
              [css.selected]: isSelected,
              [css.cardHeight]: !showImage
            })}
            elevation={elevation}>
            <CardContent
              data-type='CardContent'
              className={classNames(css.cardContent)}>
              <div className={css.cardPrimary}>
                {(library?.index || field?.field) && <DragEventPlaceholders
					dropType={library?.index || field?.field}
					id={data._id}
				/>}
                {dataMap?.images !== null && (primaryAvatar
                                              || (showImage && primaryImageDetails && !_.isEmpty(primaryImageDetails))
                                              || (showHex && !_.isEmpty(data.hex)))
                 && <div className={css.primary}
				         data-type='Primary'
                         style={primaryImageStyle}
				        >
					 <LazyAvatar
						 classes={{img: css.image}}
						 lazy={false}
						 showHex={showHex}
						 hex={data.hex}
						 className={classNames(css.image, !_.isEmpty(primaryImageDetails) ? css.cover : css.coverSelected)}
						 extra={<div className={classNames(css.diagonal)}/>}
						 src={!_.isEmpty(primaryImageDetails) ? catalog.imageUrlFor(primaryImage) : ''}
             imageId={primaryImage?.exId}

					 >
                       {showHex && data.hex ? '' : (primaryAvatar || '')}
					 </LazyAvatar>
				 </div>}
                {_.isUndefined(primaryAvatar)
                 && (showImage
                 && data.images
                 && _.isEmpty(data.images)
                 && _.isEmpty(data.hex))
                 && !hideEmptyImage && <Placeholder type={'small'}
				                                    isSelected={isSelected}
				                                    extra={<div className={classNames(css.diagonal)}/>}
				 />}

                <div className={classNames(css.titleWrapper, showImage && css.left)} data-type='titleWrapper' data-has-subtitle={subtitle1 !== null}>
                  <div className={css.titleContainer} data-type='titleContainer'>
                    <Typography variant={'body2'}
                                data-title={_.isString(title) ? title : undefined} component='div'
                                classes={{body2: css.body1}}>
                      {titleActionsLeft && <div className={css.titleActions}>{titleActionsLeft}</div>}
                      <Tooltip2 disabled={hideTooltips} className={css.titleTooltip} content={hideTooltips ? '' : (title || emptyPlaceHolder)}>
                        {shouldRenderHighlighter(title) ? <Highlighter data-type='titleText' data-searching={!_.isEmpty(searchText)} className={classNames(css.highlighter, css.titleText)} textToHighlight={title?.toString() || emptyPlaceHolder} searchWords={[searchText]}/>
                                                        : title}
                      </Tooltip2>
                      {assignedToCards}
                      {offlineItemsCount}
                      {titleCount != null &&
					   <span data-type='count' className={classNames(css.count, {[css.countVisible]: !loading})}>
                      {!_.isNumber(titleCount) ? titleCount
                                               : <NumberBadge children={titleCount}/>
                        //(viewStyle == 'flat' && level == 0 ? `${titleCount} Item${titleCount > 1 ? 's' : ''}` : `(${titleCount})`)}
                      }
                    </span>}
                      {titleActions && <div className={css.titleActions}>{titleActions}</div>}
                      {toggleInfoPanel && <ToggleInfoPanelButton infoPanelActive={infoPanelActive} toggleInfoPanel={toggleInfoPanel}/>}
                    </Typography>

                    {false && data.exId && <PrimaryIconButton onClick={e => {
                      event.stopPropagation()
                      alert('link clicked')
                    }} data-type='external-link' icon={<Link/>} className={classNames(css.linkIcon)}/>}
                  </div>
                  {(!_.isEmpty(badge1Text) || !_.isEmpty(badge2Text)) ? <div className={css.badgeContainer}>
                    {_.isEmpty(badge1Text) === false && <Badge color={'purple'} badge={badge1Text}/>}
                    {_.isEmpty(badge2Text) === false && <Badge color={'red'} badge={badge2Text}/>}
                  </div> : null}

                  {subtitle1 !== null && <Tooltip2 disabled={hideTooltips} content={hideTooltips || !subtitle1 || !_.isString(subtitle1) ? '' : subtitle1}>
					  <Typography data-type='subtitle' component='div' variant={'body1'} classes={{body1: classNames(css.secondaryText)}}>
                        {shouldRenderHighlighter(subtitle1) ? <Highlighter data-type='subtitleText' className={classNames(css.highlighter, css.truncate)} textToHighlight={subtitle1?.toString() ?? emptyPlaceHolder} searchWords={[searchText]}/>
                                                            : <span data-type='subtitleText' data-name='subtitle1' className={css.truncate}>{renderElementOrString(subtitle1) ?? emptyPlaceHolder}</span>}
                        {/* {data.exId && <PrimaryIconButton data-type='external-link' icon={<Link />} className={css.linkIcon } />} */}
					  </Typography>
				  </Tooltip2>}

                  {subtitle2 && <Tooltip2 disabled={hideTooltips} content={hideTooltips || !subtitle2 || !_.isString(subtitle2) ? '' : subtitle2}>
                    {shouldRenderHighlighter(subtitle2) ? <Highlighter className={classNames(css.highlighter, css.secondaryText)} data-name='subtitle2' textToHighlight={subtitle2?.toString() ?? emptyPlaceHolder} searchWords={[searchText]}/>
                                                        : <span data-type='subtitleText' data-name='subtitle2'>{renderElementOrString(subtitle2)}</span>}
				  </Tooltip2>}

                  {subtitle3 && <Tooltip2 disabled={hideTooltips} content={hideTooltips || !subtitle3 || !_.isString(subtitle3) ? '' : subtitle3}>
                    {shouldRenderHighlighter(subtitle3) ? <Highlighter className={classNames(css.highlighter, css.secondaryText)} data-name='subtitle3' textToHighlight={subtitle3?.toString() ?? emptyPlaceHolder} searchWords={[searchText]}/>
                                                        : <span data-type='subtitleText' data-name='subtitle3'>{renderElementOrString(subtitle3)}</span>}
				  </Tooltip2>}
                  {activeFieldDef && <div className={css.status} style={{color: active ? 'var(--green)' : 'var(--red)'}}>{this.booleanLabelFor(activeFieldDef, active === true)}</div>}

                  {extra}
                </div>
                {secondary && <ListPreviewCardSecondaryImage data={data} secondary={secondary} showImage={showImage} showHex={showHex} avatar={cardDetails?.secondary?.image?.avatar || avatar}/>}
              </div>
              {more && <div className={css.cardMoreTable}>
                {more}
			  </div>}
            </CardContent>
            {showClose && <CardActions classes={{root: css.cardActions}}>
				<ListItemSecondaryAction classes={{root: css.listActions}}>
						<IconButton title={strings.remove} onClick={onClickClose} classes={{root: css.iconButton}}>
							<Close classes={{root: css.closeIcon}}/>
						</IconButton>
				</ListItemSecondaryAction>
			</CardActions>}
          </Card>)}
      </ListItem>
    )
  }

  private booleanLabelFor = (activeFieldDef: ICatalogFieldConfig, b: boolean) => {
    const {strings} = this
    if (b) {
      return activeFieldDef?.def?.tileBooleanLabels?.trueLabel ?? strings.active
    } else {
      return activeFieldDef?.def?.tileBooleanLabels?.falseLabel ?? strings.inactive
    }
  }

  maybeRenderPopover = children => {
    const {mouseOverAssignedTo} = this

    if (false && mouseOverAssignedTo) {
      return (<Popover2
        interactionKind='hover'
        minimal={false}
        position='right'
        modifiers={{
          flip: {enabled: false}
        }}
        isOpen
        content={<ErrorBoundary>
          <BuilderCardLoader esIndex={mouseOverAssignedTo.index}
                             id={mouseOverAssignedTo.id}
                             levelOfDetail={1}
                             disableSelection hideButtons isPopover/>
        </ErrorBoundary>}
      >
        {children}
      </Popover2>)
    }

    return children
  }
}

export const ListPreviewCardSecondaryImage = observer<{ data, secondary: ICardAreaUIConfig, showImage, showHex, avatar? }>(function ListPreviewCardSecondaryImage({data, secondary, showImage, showHex, avatar}) {
  const catalog = useCatalog(),
    catalogItemsListStore = useCatalogItemsListStore(),
    searchWords = catalogItemsListStore?.sidebar?.searchWords,
    secondaryImageDetails = (_.isString(secondary?.image?.field) && _.get(data, secondary?.image?.field)) || [],
    {strings} = catalog

  return (avatar && secondaryImageDetails
          || (showImage && secondaryImageDetails && !_.isEmpty(secondaryImageDetails))
          || (showHex && !_.isEmpty(data.hex)))
         && <div className={css.secondary}
                 data-type='Secondary'
                 style={{'--swatch-size': secondary?.image?.swatchSize ? `${secondary?.image?.swatchSize}px` : undefined} as any}>
	         <LazyAvatar
		         classes={{img: css.image}}
		         lazy={false}
		         showHex={showHex}
		         hex={data.hex}
		         className={classNames(css.secondaryImage, !_.isEmpty(secondaryImageDetails) ? css.cover : css.coverSelected)}
		         src={!_.isEmpty(secondaryImageDetails) ? catalog.imageUrlFor(_.isArray(secondaryImageDetails) ? _.first(secondaryImageDetails) : secondaryImageDetails) : ''}
	         >{showHex && data.hex ? '' : (avatar || '')}</LazyAvatar>
	         <div className={css.sections}>
               {secondary.sections?.map(({fields}, i) =>
                 <div className={css.section} key={i}>
                   {fields?.map(({field, style}, j) =>
                       <div className={css.field} data-field={field} data-style={style}
                            title={catalogItemsListStore?.libraryWorkspace ? catalog.labelFor_workspace(field, catalogItemsListStore?.libraryWorkspace, strings.noValue) : catalog.labelForFieldName_currentCatalog(field)}
                            key={j}>
                         <Highlighter className={css.caption} textToHighlight={(_.get(data, field) || emptyPlaceHolder).toString()} searchWords={searchWords ?? []}/>
                       </div>)}
                 </div>
               )}
	         </div>
         </div>
})

/**
 * Tooltip will throw exception is wrapping a simple string/number/etc
 * @param v
 */
const shouldRenderHighlighter = (v: any) => {
  return _.isString(v) || _.isNumber(v) || _.isNull(v) || _.isUndefined(v)
}

const renderElementOrString = (v) => {
  return React.isValidElement(v) ? v : v?.toString()
}

export const ListPreviewCardRefCatalogItem = observer<{ className?: string, field: ICatalogFieldConfig, value?: any }>(function ListPreviewCardRefCatalogItem({className, field, value}) {
  const catalog = useCatalog(),
    id = catalog.idFor(value),
    workspace = catalog.workspaceForField(field),
    dataMap = workspace.dataMap

  return (
    <div className={classNames(css.refCatalogItem, className)}
         data-field={field?.field}
         data-id={id}
    >
      <DataMapper config={dataMap} data={value}>
        <ListPreviewCard stores={catalog.stores} workspace={workspace?.config} dataMap={dataMap} field={field}/>
      </DataMapper>
      {/*<BuilderCardLoader id={id} workspace={workspace} field={field} data={value} hideButtons levelOfDetail={0} />*/}
    </div>
  )
})

export const ListPreviewCardRefListCatalogItems = observer<{ className?: string, field: ICatalogFieldConfig, value?: Array<any> }>(function ListPreviewCardRefListCatalogItems({className, field, value}) {
  const catalog = useCatalog(),
    workspace = catalog.workspaceForField(field),
    arr = _.flatMap(_.isArray(value) ? value : !value ? [] : [value]).filter(_.identity)
  return (
    <div className={classNames(css.refListCatalogItems, className)}
         data-field={field?.field}
         data-count={value?.length}
    >
      {arr.map((v, i) => {
        const id = catalog.idFor(v)
        if (id) {
          return (
            <div key={i} className={css.cardWrapper} data-id={id} onClick={utils.eatMouseEvent}>
              <BuilderCardLoader readOnly noSelect noFocus noHover hideButtons levelOfDetail={0} disableSelection id={id} workspace={workspace?.config} field={field} data={v} esIndex={field.esIndex}/>
            </div>)
        } else {
          console.info(`No id found for ref list item`, {v, value, arr})
          return null
        }
      })}
    </div>
  )
})



// WEBPACK FOOTER //
// ./src/shared/components/ui/components/ListPreviewCard.tsx